"use client";

/**
 * Site analytics loader (client component).
 *
 * Injects at most one privacy-first analytics script, and only when ALL of the
 * following hold:
 *   - NODE_ENV is production, AND
 *   - the live request host is an indexable production host
 *     (resolved at runtime from `window.location.host`, so a single build
 *     artifact served on dev/staging/preview/localhost never loads anything),
 *     AND
 *   - a provider is explicitly opted in via NEXT_PUBLIC_ANALYTICS_PROVIDER and
 *     its required config is present.
 *
 * Because the host is read client-side from `window.location.host`, rendering
 * this in the root layout does NOT force the layout to be dynamic — the layout
 * stays statically rendered and there is no `headers()` call. (An optional
 * `host` prop is accepted for callers that already know the host server-side,
 * e.g. from `headers()`, but the layout intentionally does not pass it.)
 *
 * The component renders no DOM of its own and injects the script imperatively
 * after mount, so there is never an SSR/CSR hydration mismatch. An id guard
 * makes injection idempotent across React StrictMode double-mounts and client
 * navigations, so we never load the script — or double-count a page-view —
 * twice. No cookies, no user IDs, no personal data are ever set.
 */
import { useEffect } from "react";
import {
  getAnalyticsProvider,
  resolveAnalyticsScript,
  shouldLoadAnalytics,
} from "@/lib/analytics";

const SCRIPT_ID = "fc-site-analytics";

export function SiteAnalytics({ host }: { host?: string | null }) {
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Runtime host decision. Prefer an explicitly passed host; otherwise read
    // the live host from the browser.
    const liveHost = host ?? window.location.host;
    if (!shouldLoadAnalytics(liveHost)) return;

    const provider = getAnalyticsProvider();
    if (provider === "none") return;

    const script = resolveAnalyticsScript(provider);
    if (!script) return;

    // Idempotent: never inject twice (StrictMode remount, SPA navigation).
    if (document.getElementById(SCRIPT_ID)) return;

    const el = document.createElement("script");
    el.id = SCRIPT_ID;
    el.src = script.src;
    el.defer = true;
    for (const [key, value] of Object.entries(script.attrs)) {
      el.setAttribute(key, value);
    }
    document.head.appendChild(el);

    // Intentionally no cleanup that removes the script: analytics is a
    // page-lifetime concern. Removing it on a StrictMode unmount only to
    // re-add it on remount would risk a double page-view; the id guard above
    // already prevents genuine duplicates.
  }, [host]);

  return null;
}
